import { Chip, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

/**
 * A demand inside an override row, coloured by whether the override actually WORKED.
 *
 * The generic DemandChip's "added by an override" highlight is tautological here — every demand
 * in an override was, by definition, added by one. The useful question in this section is
 * whether it survived the pipeline, because an override can be perfectly valid and still be
 * stripped downstream by the role filter or the server_demands gate.
 */
export function GrantDemandChip({ item: demand }) {
    const { breakdown } = useBoundContext()
    const inEffect = (breakdown?.final ?? []).includes(demand)
    const forced = (breakdown?.forced ?? []).find(matchesDemand)
    const removal = (breakdown?.removed ?? []).find(matchesDemand)

    return (
        <Tooltip title={explanation()} placement="top" arrow>
            <Chip
                size="small"
                label={demand}
                variant={inEffect && !forced ? "outlined" : "filled"}
                color={color()}
                data-cy={`grant-demand-${demand}`}
                data-effect={effect()}
            />
        </Tooltip>
    )

    function color() {
        // Amber for forced: in effect, but only because the override overrules a filter. That
        // is a different thing from an override nothing objected to, and worth seeing at a glance.
        if (forced) return "warning"
        return inEffect ? "success" : "error"
    }

    function effect() {
        if (forced) return "forced"
        return inEffect ? "in-effect" : "not-in-effect"
    }

    function explanation() {
        if (forced) {
            return `In effect, but only because this override forces it past ${forced.wouldBeRemovedBy}. ${forced.reason}`
        }
        if (inEffect) return "In effect — this demand reaches the user."
        if (removal) return `Not in effect. Removed by ${removal.removedBy}. ${removal.reason}`
        return "Not in effect — this demand does not reach the user."
    }

    function matchesDemand(entry) {
        return entry.demand === demand
    }
}
